// Agents Inbox reads — carved from `src/features/inbox/hooks.ts` (now a
// delegate; ARCFE-1051 · D2).
//
// HEADLESS: the pre-move hooks computed their own `enabled` gate from
// `useIsProjectsEnabled()`, the redux auth token, `useUser()` and
// `authTokenSyncedAtom` — a flag, the app store and an app atom, none of which
// may live in `/data`. Per dimension 3 the gate crosses as an explicit
// parameter and the delegate computes it, so the flag stays app-resolved and
// the INC-215 boot-window protection keeps its exact shape.
//
// `usePendingApprovalsForRun` DID NOT cross at ARCFE-1051 and now does
// (ARCFE-1079). It maps inbox items through `inboxItemToPendingApproval` into
// `PendingToolApproval`, a contract that then lived in
// `src/features/tool-approvals` — the HARNESS bucket (task 4.6) — so carving it
// would have dragged a harness contract into an independent family ahead of its
// own milestone. ARCFE-1059 moved that contract into this package's `/contracts`
// door, which is exactly the condition the exclusion named.
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { inboxQueryKeys, type InboxItemsParams } from '../../contracts';
import type { PendingToolApproval } from '../../contracts/tool-approval.types';
import { fetchInboxItems } from '../api/inbox';
import { inboxItemToPendingApproval } from '../inbox/adapters';

/**
 * Shared params for the global pending-inbox list used by the sidebar
 * attention dot. The explicit ``limit`` keeps a large pending backlog
 * from being truncated to the API's default page size — which would
 * silently miss sidebar dots.
 */
export const PENDING_INBOX_PARAMS: InboxItemsParams = {
  status: ['pending'],
  limit: 100,
};

interface InboxItemsArgs {
  params?: InboxItemsParams;
  /**
   * App-resolved gate. Composed by the delegate from the projects flag, the
   * auth token and user/auth-token-sync readiness; `params !== undefined`
   * stays here because it is data behaviour, not app policy.
   */
  enabled: boolean;
}

export const useInboxItems = ({ params, enabled }: InboxItemsArgs) => {
  return useQuery({
    queryKey: inboxQueryKeys.items(params),
    queryFn: () => fetchInboxItems(params),
    enabled: enabled && params !== undefined,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
};

interface InboxItemsForRunArgs {
  agentRunId: string | null | undefined;
  enabled: boolean;
}

export const useInboxItemsForRun = ({ agentRunId, enabled }: InboxItemsForRunArgs) => {
  const params: InboxItemsParams | undefined = agentRunId
    ? { agent_run_id: agentRunId, status: ['pending'] }
    : undefined;
  return useQuery({
    queryKey: inboxQueryKeys.items(params),
    queryFn: () => fetchInboxItems(params),
    enabled: enabled && params !== undefined,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Project IDs that currently have a pending approval, derived from the
 * global pending-inbox query. The sidebar uses this to mark which chat
 * rows need the user's attention — ``project_id`` on an inbox item maps
 * 1:1 to the ``chat.id`` of its v2 agent chat row.
 *
 * Stays in sync via ``inbox.dirty`` SSE invalidation.
 */
export const useProjectIdsNeedingAttention = ({
  enabled,
}: {
  enabled: boolean;
}): Set<string> => {
  const { data } = useInboxItems({ params: PENDING_INBOX_PARAMS, enabled });
  return useMemo(() => {
    const ids = new Set<string>();
    for (const item of data?.items ?? []) {
      const projectId = item.render.domain.project_id;
      if (projectId) ids.add(projectId);
    }
    return ids;
  }, [data?.items]);
};

/**
 * Pending approvals for one run, in the `PendingToolApproval` shape the
 * harness renderers already take. Same app-resolved gate as
 * `useInboxItemsForRun`, which the delegate composes.
 */
export const usePendingApprovalsForRun = (
  args: InboxItemsForRunArgs
): PendingToolApproval[] => {
  const { data } = useInboxItemsForRun(args);
  return useMemo(
    () => (data?.items ?? []).map(inboxItemToPendingApproval),
    [data?.items]
  );
};

export const usePendingApprovalRunIds = ({
  agentRunIds,
  enabled,
}: {
  agentRunIds: Array<string | null | undefined>;
  enabled: boolean;
}): ReadonlySet<string> => {
  const uniqueRunIds = useMemo(
    () => [...new Set(agentRunIds.filter((id): id is string => Boolean(id)))],
    [agentRunIds]
  );
  return useQueries({
    queries: uniqueRunIds.map((agentRunId) => {
      const params: InboxItemsParams = {
        agent_run_id: agentRunId,
        status: ['pending'],
      };
      return {
        queryKey: inboxQueryKeys.items(params),
        queryFn: () => fetchInboxItems(params),
        enabled,
        staleTime: 5_000,
        refetchOnWindowFocus: true,
      };
    }),
    combine: (queries) => {
      const ids = new Set<string>();
      queries.forEach((query, index) => {
        const runId = uniqueRunIds[index];
        if (!runId) return;
        // Keep terminal runs visible while approval state is loading or unavailable.
        if (
          enabled &&
          (query.status !== 'success' || (query.data?.items.length ?? 0) > 0)
        ) {
          ids.add(runId);
        }
      });
      return ids;
    },
  });
};

/**
 * Pending run ids already proven by a run-scoped inbox read elsewhere.
 *
 * This deliberately never fetches. Routine list surfaces use the single global
 * inbox request for their initial attention state, then consult these cached
 * run-scoped responses after a chat has been opened. That keeps the indicator
 * durable across a global-list refetch without turning a list of routines into
 * one inbox request per row.
 */
export const useCachedPendingApprovalRunIds = (
  agentRunIds: Array<string | null | undefined>
): ReadonlySet<string> => {
  const uniqueRunIds = useMemo(
    () => [...new Set(agentRunIds.filter((id): id is string => Boolean(id)))],
    [agentRunIds]
  );
  return useQueries({
    queries: uniqueRunIds.map((agentRunId) => {
      const params: InboxItemsParams = {
        agent_run_id: agentRunId,
        status: ['pending'],
      };
      return {
        queryKey: inboxQueryKeys.items(params),
        queryFn: () => fetchInboxItems(params),
        enabled: false,
      };
    }),
    combine: (queries) => {
      const ids = new Set<string>();
      queries.forEach((query, index) => {
        const runId = uniqueRunIds[index];
        // React Query preserves the last successful data through a refetch or
        // refetch error. Read that data directly: only a later successful
        // empty response replaces it and clears the hand.
        if (runId && (query.data?.items.length ?? 0) > 0) {
          ids.add(runId);
        }
      });
      return ids;
    },
  });
};
